import { useCallback, useEffect, useRef, useState } from 'react';
import { broadcasterLabel, TERMINAL_STATUSES, type StreamStatus } from '@zip2live/shared/stream-state';
import type { OwnedStream } from '@zip2live/shared';
import { Header, Link, LiveBadge } from '../components/Brand.tsx';
import { StreamStats, WatchingPill } from '../components/StreamStats.tsx';
import { CameraIcon, CloseIcon, FlipIcon, MicIcon, ScreenIcon } from '../components/Icons.tsx';
import { ShareBar } from '../components/ShareBar.tsx';
import { ChatOverlay, ChatPanel, ChatSheet } from '../components/Chat.tsx';
import { TitleEditor, TitleInput } from '../components/TitleEditor.tsx';
import { api, ApiError } from '../lib/api.ts';
import { ensureSession } from '../lib/auth.ts';
import { ensureAgreed, forgetAgreement, isRulesError } from '../lib/rules.ts';
import { useStreamStatus } from '../lib/useStreamStatus.ts';
import { PublishError, WhipPublisher, webrtcSupported } from '../lib/whip.ts';
import { refreshMe, useMe } from '../lib/me.ts';
import { ListedSwitch } from '../components/ListedSwitch.tsx';
import { NotifyFollowersSwitch } from '../components/FollowButton.tsx';
import { SignInSheet } from '../components/SignInFlow.tsx';
import { openBroadcast, resumePath } from '../components/LiveReturn.tsx';
import { guardLeave } from '../lib/router.ts';

type Phase = 'camera' | 'ready' | 'starting' | 'broadcasting' | 'ended';

/** No more video goes to a stream in these. */
const isOver = (status: string) => status === 'ENDING' || TERMINAL_STATUSES.has(status as StreamStatus);

function cameraErrorMessage(err: unknown): string {
  const name = (err as DOMException)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Camera access is blocked. Allow camera and microphone in your browser settings, then try again.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No camera found. You can still go live by sharing your screen.';
  if (name === 'NotReadableError') return 'Your camera is in use by another app. Close it and try again.';
  return 'Could not start your camera.';
}

export function GoLive() {
  const preview = useRef<HTMLVideoElement>(null);
  const media = useRef<MediaStream | null>(null);
  const publisher = useRef<WhipPublisher | null>(null);
  const owned = useRef<OwnedStream | null>(null);
  /** The pending reconnect attempt, and how many failed in a row (for backoff). */
  const retry = useRef<{ timer: ReturnType<typeof setTimeout> | null; failures: number }>({ timer: null, failures: 0 });
  /** Streams this page is done with (ended, removed, refused): never offered back. */
  const finished = useRef(new Set<string>());
  const [phase, setPhase] = useState<Phase>('camera');
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const [micOn, setMicOn] = useState(true);
  const [screen, setScreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stream, setStreamInfo] = useState<OwnedStream | null>(null);
  const [chatSheet, setChatSheet] = useState(false);
  const { stream: status, setStream: setStatus } = useStreamStatus(stream?.id ?? null, stream);
  const { me } = useMe();
  /** This page's video isn't reaching the server: the connection dropped and is being brought back. */
  const [dropped, setDropped] = useState(false);
  /** Still not back after several tries (no network, or the server can't be reached). */
  const [stuck, setStuck] = useState(false);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  /** The stream was picked up on another tab or device: only one place sends its video. */
  const [elsewhere, setElsewhere] = useState(false);
  /** ?resume=<id> ("Back to your stream", a reload, a reopened tab): picked up once the camera is on. */
  const [pendingResume, setPendingResume] = useState(() => new URLSearchParams(location.search).get('resume'));
  const [resuming, setResuming] = useState<string | null>(null);
  /** "Start a new stream instead" of picking up the one that's still on. */
  const [freshStart, setFreshStart] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Show on Live Now: off (unlisted) unless the creator turns it on; it can be flipped mid-stream.
  const [listed, setListed] = useState(false);
  const [listing, setListing] = useState(false);
  const canList = Boolean(me?.user?.isVerified);
  // Title for the stream about to start: the creator's last one until they type.
  const [title, setTitle] = useState('');
  const titleTouched = useRef(false);
  const defaultTitle = me?.channel?.defaultTitle;
  useEffect(() => {
    if (!titleTouched.current && defaultTitle) setTitle(defaultTitle);
  }, [defaultTitle]);
  // "Notify followers" for this broadcast: on by default, every time.
  const followers = me?.channel?.followers ?? 0;
  const [notifyFollowers, setNotifyFollowers] = useState(true);
  const [told, setTold] = useState<string | null>(null);

  const startCamera = useCallback(async (mode: 'user' | 'environment') => {
    setError(null);
    if (!webrtcSupported()) {
      setError('This browser cannot broadcast. Try a recent Chrome, Safari, Edge or Firefox.');
      return;
    }
    try {
      const next = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const old = media.current;
      media.current = next;
      if (preview.current) preview.current.srcObject = next;
      if (publisher.current) {
        await publisher.current.replaceTrack('video', next.getVideoTracks()[0] ?? null);
        await publisher.current.replaceTrack('audio', next.getAudioTracks()[0] ?? null);
      }
      old?.getTracks().forEach((t) => t.stop());
      next.getAudioTracks().forEach((t) => (t.enabled = micOn));
      setPhase((p) => (p === 'camera' ? 'ready' : p));
    } catch (err) {
      setError(cameraErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    // Wake streaming capacity while the camera preview starts (no-op if it is already running).
    void fetch('/api/v1/warm', { method: 'POST' }).catch(() => undefined);
    void startCamera('user');
    // Current, not cached: a stream that's still on (closed tab, reload) is offered back below.
    void refreshMe();
    return () => {
      if (retry.current.timer) clearTimeout(retry.current.timer);
      media.current?.getTracks().forEach((t) => t.stop());
      void publisher.current?.stop();
    };
  }, [startCamera]);

  /** Send the camera to the stream. Drops are brought back by `reconnect`; viewers stay on the same link. */
  const publish = async (s: OwnedStream) => {
    const pub = new WhipPublisher(s.publish.whipUrl, s.publish.token);
    publisher.current = pub;
    pub.pc.addEventListener('connectionstatechange', () => {
      if (publisher.current !== pub || !owned.current) return;
      const st = pub.pc.connectionState;
      if (st === 'connected') {
        retry.current.failures = 0;
        setDropped(false);
        setStuck(false);
      } else if (st === 'failed' || st === 'disconnected') {
        setDropped(true);
        // "disconnected" often recovers by itself within seconds; "failed" never does.
        reconnect(st === 'failed' ? 0 : 4000);
      }
    });
    await pub.publish(media.current!);
  };

  /**
   * Put the video back on the stream after a drop (wifi blip, switching networks, a server restart).
   * Keeps trying, backing off to every 15 s, until it works or the stream is over: the server holds
   * the stream for a minute after its video stops, then ends it, and the status says so.
   */
  const reconnect = (delay: number) => {
    const r = retry.current;
    if (r.timer) clearTimeout(r.timer);
    r.timer = setTimeout(() => {
      r.timer = null;
      const s = owned.current;
      const old = publisher.current;
      if (!s || old?.pc.connectionState === 'connected') return;
      void old?.stop();
      publish(s).catch((err: unknown) => {
        if (owned.current !== s) return;
        // Turned away: the stream ended, or it's being sent from another tab or device now.
        if (err instanceof PublishError && err.code === 'unauthorized') return void refused(s.id);
        if (++r.failures >= 4) setStuck(true);
        reconnect(Math.min(15_000, 1000 * 2 ** r.failures));
      });
    }, delay);
  };

  useEffect(() => {
    const again = () => {
      if (!owned.current || publisher.current?.pc.connectionState === 'connected') return;
      retry.current.failures = 0;
      reconnect(0);
    };
    const online = () => {
      setOffline(false);
      again();
    };
    const offlineNow = () => setOffline(true);
    // Back from another app (phones often drop the connection meanwhile).
    const visible = () => document.visibilityState === 'visible' && again();
    // Tab closing or reloading: say so now, so viewers see it reconnecting instead of a frozen
    // picture until the connection times out. (Kept in memory instead? `pageshow` picks it up again.)
    const hide = () => {
      if (owned.current) void publisher.current?.stop();
    };
    const show = (e: PageTransitionEvent) => e.persisted && again();
    addEventListener('online', online);
    addEventListener('offline', offlineNow);
    document.addEventListener('visibilitychange', visible);
    addEventListener('pagehide', hide);
    addEventListener('pageshow', show);
    return () => {
      removeEventListener('online', online);
      removeEventListener('offline', offlineNow);
      document.removeEventListener('visibilitychange', visible);
      removeEventListener('pagehide', hide);
      removeEventListener('pageshow', show);
    };
  }, []);

  /** This page stops sending video: the stream ended, was removed, or continues somewhere else. */
  const stopSending = () => {
    const s = owned.current;
    if (s) finished.current.add(s.id);
    owned.current = null;
    if (retry.current.timer) clearTimeout(retry.current.timer);
    retry.current.timer = null;
    retry.current.failures = 0;
    void publisher.current?.stop();
    publisher.current = null;
    setDropped(false);
    setStuck(false);
    setPhase('ended');
  };

  /** The ingest server turned this page's video away. Ended (the status will say), or picked up elsewhere. */
  const refused = async (id: string) => {
    const s = await api.getStream(id).catch(() => null);
    if (owned.current?.id !== id) return;
    if (s && !isOver(s.status)) setElsewhere(true);
    stopSending();
  };

  /** The link is on screen from here on, before any video flows. */
  const begin = async (s: OwnedStream) => {
    owned.current = s;
    setStreamInfo(s);
    setElsewhere(false);
    setNotice(null);
    setPhase('broadcasting');
    try {
      await publish(s);
    } catch (err) {
      if (owned.current !== s) return;
      if (err instanceof PublishError && err.code === 'unauthorized') return void refused(s.id);
      // A machine still booting, a flaky network: keep trying, like any drop.
      setDropped(true);
      reconnect(2000);
    }
  };

  const goLive = async () => {
    if (!media.current) return;
    // The first time: ZIP2LIVE's rules and your age (18 and over to go live).
    if (!(await ensureAgreed('live'))) return;
    setPhase('starting');
    setError(null);
    try {
      await ensureSession();
      // Untouched and empty (e.g. the last title has not loaded yet): the API uses the last title itself.
      const s = await api.createStream(
        {
          source: 'browser',
          visibility: listed && canList ? 'public' : 'unlisted',
          title: titleTouched.current || title ? title.trim() : undefined,
          notifyFollowers,
        },
        crypto.randomUUID(),
      );
      void refreshMe();
      await begin(s);
    } catch (err) {
      // This account hasn't agreed (it agreed in another browser as someone else): ask next time.
      if (isRulesError(err)) forgetAgreement();
      setError(err instanceof ApiError ? err.message : 'Could not reach the streaming server. Check your connection and try again.');
      if (!owned.current) setPhase('ready');
    }
  };

  /** Pick a stream that's still on back up here: same link, new publish key (the old one stops working). */
  const resume = async (id: string) => {
    if (!media.current) return;
    setPhase('starting');
    setResuming(id);
    setError(null);
    finished.current.delete(id);
    try {
      await ensureSession();
      const s = await api.resumeStream(id);
      setNotifyFollowers(s.notifyFollowers ?? true);
      await begin(s);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 409 || status === 404 || status === 403) finished.current.add(id);
      setError(
        status === 409 ? (err as ApiError).message
        : status === 403 || status === 404 ? 'That stream was started on another account. Sign in with that one to continue it.'
        : err instanceof ApiError ? err.message : 'Could not reach the streaming server. Check your connection and try again.',
      );
      if (!owned.current) setPhase('ready');
    } finally {
      setResuming(null);
    }
  };

  useEffect(() => {
    if (!pendingResume || phase !== 'ready') return;
    setPendingResume(null);
    void resume(pendingResume);
  }, [pendingResume, phase]);

  const end = async () => {
    const s = owned.current;
    owned.current = null;
    // Mark the stream ending first, so the ingest side finalizes right away instead of
    // treating the disconnect as a network drop and waiting out the reconnect window.
    if (s) await api.endStream(s.id).catch(() => undefined);
    if (s) finished.current.add(s.id);
    stopSending();
  };

  const toggleScreen = async () => {
    if (!screen) {
      try {
        const display = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: false });
        const track = display.getVideoTracks()[0]!;
        track.addEventListener('ended', () => void startCamera(facing).then(() => setScreen(false)));
        await publisher.current?.replaceTrack('video', track);
        if (preview.current) preview.current.srcObject = new MediaStream([track, ...(media.current?.getAudioTracks() ?? [])]);
        media.current = new MediaStream([track, ...(media.current?.getAudioTracks() ?? [])]);
        setScreen(true);
        setPhase((p) => (p === 'camera' ? 'ready' : p));
      } catch {
        /* user cancelled the picker */
      }
    } else {
      await startCamera(facing);
      setScreen(false);
    }
  };

  // The status of this page's stream (the hook still holds the last stream's until the new one loads).
  const current = status && stream && status.id === stream.id ? status : null;
  // Taken down by moderation, or ended from elsewhere (the account page, a newer GO LIVE, the time
  // limit, the reconnect window running out): stop sending and say so.
  const removed = Boolean(current?.removed);
  const over = Boolean(current && isOver(current.status));
  useEffect(() => {
    if ((removed || over) && owned.current) stopSending();
  }, [removed, over]);

  const live = current?.status === 'LIVE';
  const toggleListed = async (next: boolean) => {
    if (!stream) return setListed(next);
    setListing(true);
    try {
      await api.setListed(stream.id, next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t change that. Try again.');
    } finally {
      setListing(false);
    }
  };

  // Once live, say what happened to the alert (a moment later, once it's been sent).
  const streamId = stream?.id;
  useEffect(() => {
    if (!live || !streamId) return setTold(null);
    const t = setTimeout(() => {
      void api.notifyFollowers(streamId).then((n) => {
        const r = n.run;
        if (!r || !n.followers) return setTold(null);
        if (r.status === 'throttled') return setTold('Followers were told about a stream less than 30 minutes ago, so not again.');
        if (r.status === 'skipped') return setTold(r.reason === 'creator_off' ? 'Followers weren’t notified.' : null);
        setTold(`We’re telling your ${n.followers === 1 ? 'follower' : `${n.followers} followers`} you’re live.`);
      }, () => undefined);
    }, 2000);
    return () => clearTimeout(t);
  }, [live, streamId]);
  const switchNotify = (on: boolean) => {
    setNotifyFollowers(on);
    if (owned.current) void api.setNotifyFollowers(owned.current.id, on).catch(() => setNotifyFollowers(!on));
  };

  // Continued elsewhere: this page isn't the one on air, whatever the stream's status says.
  const label = elsewhere && phase === 'ended' ? null : dropped ? 'Reconnecting…' : current ? broadcasterLabel(current.status) : phase === 'starting' ? 'Preparing…' : phase === 'ready' ? 'Camera ready' : null;
  const elapsed = useElapsed(live && !dropped, current?.startedAt ?? null);
  const onAir = phase === 'broadcasting' && stream;
  const sending = Boolean(onAir);

  // While sending: a reload or a reopened tab comes back to this stream, and leaving asks first.
  useEffect(() => {
    if (location.pathname !== '/go-live') return;
    const url = sending && streamId ? resumePath(streamId) : phase === 'ended' ? '/go-live' : null;
    if (url && location.pathname + location.search !== url) history.replaceState(null, '', url);
  }, [sending, streamId, phase]);
  useEffect(() => {
    if (!sending || !streamId) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    addEventListener('beforeunload', warn);
    const lift = guardLeave(resumePath(streamId), () => confirm('Leave your live stream? Your video stops, and the stream ends in about a minute unless you come back to it.'));
    return () => {
      removeEventListener('beforeunload', warn);
      lift();
    };
  }, [sending, streamId]);

  // A stream of this person's that's still on (closed tab, reload, left to sign in): offered back first.
  const waiting = !stream && !freshStart ? openBroadcast(me, finished.current) : null;
  const comeBack = phase === 'camera' || phase === 'ready' || phase === 'starting' ? (resuming ?? pendingResume ?? waiting?.id ?? null) : null;
  const comeBackInfo = comeBack ? me?.streams.find((s) => s.id === comeBack) ?? null : null;

  // Signed in from this page: the stream (and anything else this device streamed) is the account's now.
  const signedIn = () => {
    setSigningIn(false);
    const s = owned.current;
    if (!s) return;
    setNotice('You’re signed in. This stream is saved to your account.');
    void api.getStream(s.id).then(setStatus, () => undefined);
  };

  const toggleMic = () => {
    const next = !micOn;
    media.current?.getAudioTracks().forEach((t) => (t.enabled = next));
    setMicOn(next);
  };
  const flip = () => {
    const next = facing === 'user' ? 'environment' : 'user';
    setFacing(next);
    void startCamera(next);
  };
  const canShare = 'getDisplayMedia' in (navigator.mediaDevices ?? {});

  // Retitle while live or after. Open watch pages get it through the stream room; a live stream's
  // title is also what the next one starts with.
  const saveTitle = async (next: string | null) => {
    if (!stream) return;
    const updated = await api.setTitle(stream.id, next);
    setStatus(updated);
    if (phase === 'broadcasting') {
      titleTouched.current = true;
      setTitle(next ?? '');
    }
  };
  const currentTitle = current?.title ?? null;

  const tools = (compact: boolean) => (
    <div className={`flex justify-center ${compact ? 'gap-2' : 'gap-7'}`}>
      <ToolButton compact={compact} label={micOn ? 'Mic on' : 'Mic off'} pressed={!micOn} onClick={toggleMic}>
        <MicIcon off={!micOn} />
      </ToolButton>
      <ToolButton compact={compact} label="Flip" disabled={screen} onClick={flip}>
        <FlipIcon />
      </ToolButton>
      {canShare && (
        <ToolButton compact={compact} label={screen ? 'Camera' : 'Screen'} onClick={() => void toggleScreen()}>
          {screen ? <CameraIcon /> : <ScreenIcon />}
        </ToolButton>
      )}
    </div>
  );

  return (
    <main className="flex h-[100dvh] flex-col bg-navy-900 text-white sm:h-auto sm:min-h-full">
      <div className="hidden sm:block">
        <Header tone="dark" streaming onSignIn={() => setSigningIn(true)} />
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col sm:mx-auto sm:w-full sm:max-w-6xl sm:flex-none sm:flex-row sm:items-start sm:gap-8 sm:px-8 sm:pt-2 sm:pb-12">
        {/* Stage: full-bleed camera on phones, a framed 16:9 preview on larger screens. */}
        <div className="relative min-h-0 flex-1 overflow-hidden bg-[#12234a] sm:aspect-video sm:min-w-0 sm:rounded-2xl">
          <video
            ref={preview}
            autoPlay
            muted
            playsInline
            className={`absolute inset-0 h-full w-full object-cover sm:object-contain ${facing === 'user' && !screen ? '-scale-x-100' : ''}`}
          />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-2 p-3.5 pt-[max(0.875rem,env(safe-area-inset-top))]">
            <div className="flex items-center gap-2">
              {/* While live, END STREAM is the way out: closing here would just drop the video. */}
              {!onAir && (
                <Link to="/" aria-label="Close" className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/45 sm:hidden">
                  <CloseIcon />
                </Link>
              )}
              {live && !dropped && onAir ? (
                <>
                  <LiveBadge />
                  {elapsed && <span className="rounded-lg bg-black/50 px-2.5 py-1 text-sm font-bold tabular-nums">{elapsed}</span>}
                  {current && <WatchingPill viewers={current.viewers} />}
                </>
              ) : label ? (
                <span className="rounded-full bg-black/50 px-3.5 py-2 text-sm font-bold">{label}</span>
              ) : null}
            </div>
            {onAir && <div className="sm:hidden">{tools(true)}</div>}
          </div>
          {phase === 'camera' && !error && <div className="absolute inset-0 flex items-center justify-center font-semibold text-white/70">Starting camera…</div>}
        </div>

        {/* Controls: a bottom sheet over the camera on phones, a side panel on larger screens. */}
        <div className="absolute inset-x-0 bottom-0 sm:static sm:w-[360px] sm:shrink-0">
          {/* Phones: the latest chat floats over the camera; tap for the full chat and host tools. */}
          {onAir && current && (
            <div className="px-3.5 pb-1.5 sm:hidden">
              <ChatOverlay stream={current} onOpen={() => setChatSheet(true)} className="max-h-[28dvh]" emptyHint="Chat shows up here. Tap to open." />
            </div>
          )}
          {error && (
            <p role="alert" className="mx-3 mb-2 rounded-xl bg-race px-4 py-3 text-sm font-semibold sm:mx-0 sm:mb-4">
              {error}
            </p>
          )}

          {comeBack && (
            <div className="bg-navy-900/75 px-5 pt-5 pb-[max(1.75rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:rounded-3xl sm:bg-navy sm:p-7">
              <p className="speed-text text-2xl">Your stream is still on.</p>
              <p className="mt-1.5 text-[15px] text-muted-dark">
                {phase === 'ready' || phase === 'starting' ? 'Pick it up here. Viewers stay on the same link.' : 'It picks up here as soon as your camera is on. Viewers stay on the same link.'}
              </p>
              {comeBackInfo?.title && <p className="mt-3 truncate font-extrabold">{comeBackInfo.title}</p>}
              <p className="mt-1 truncate font-mono text-sm text-faint-dark">{location.host}/s/{comeBack}</p>
              <button
                type="button"
                onClick={() => void resume(comeBack)}
                disabled={phase !== 'ready'}
                className="btn-race mt-6 h-[72px] w-full text-[24px] sm:text-[28px] sm:shadow-race-deep"
              >
                <span className="h-3 w-3 rounded-full bg-white" aria-hidden />
                {phase === 'ready' ? 'CONTINUE STREAM' : 'PREPARING…'}
              </button>
              {!resuming && !pendingResume && (
                <button type="button" onClick={() => setFreshStart(true)} className="mt-4 h-11 w-full text-[15px] font-bold underline underline-offset-4">
                  Start a new stream instead
                </button>
              )}
            </div>
          )}

          {(phase === 'ready' || phase === 'starting' || phase === 'camera') && !comeBack && (
            <div className="bg-navy-900/75 px-5 pt-5 pb-[max(1.75rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:rounded-3xl sm:bg-navy sm:p-7">
              <p className="speed-text mb-5 hidden text-2xl sm:block">Ready when you are.</p>
              <TitleInput
                value={title}
                onChange={(v) => {
                  titleTouched.current = true;
                  setTitle(v);
                }}
                tone="dark"
                label="Stream title (optional)"
                className="mb-5"
              />
              {tools(false)}
              <div className="mt-5 text-white">
                <ListedSwitch listed={listed && canList} canList={canList} onChange={setListed} tone="dark" onSignIn={() => setSigningIn(true)} />
              </div>
              {followers > 0 && (
                <div className="mt-5">
                  <NotifyFollowersSwitch on={notifyFollowers} followers={followers} onChange={setNotifyFollowers} />
                </div>
              )}
              <button
                type="button"
                onClick={() => void goLive()}
                disabled={phase !== 'ready'}
                className="btn-race mt-6 h-[72px] w-full text-[32px] sm:shadow-race-deep"
              >
                <span className="h-3 w-3 rounded-full bg-white" aria-hidden />
                {phase === 'starting' ? 'PREPARING…' : 'GO LIVE'}
              </button>
              <p className="mt-3 text-center text-[13px] text-muted-dark">
                {freshStart && openBroadcast(me, finished.current) ? 'Your other stream ends when this one starts.' : 'Your link appears the moment you tap.'}
              </p>
            </div>
          )}

          {onAir && (
            <div className="m-2 rounded-[28px] bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] text-navy sm:m-0 sm:rounded-3xl sm:p-7">
              <div className="mx-auto -mt-1 mb-3 h-1.5 w-10 rounded-full bg-line sm:hidden" aria-hidden />
              <p className="speed-text text-2xl">{dropped ? 'Reconnecting…' : live ? 'You’re live.' : 'Your link is ready.'}</p>
              <p className="mt-1 text-[15px] text-muted" role={dropped ? 'status' : undefined}>
                {dropped
                  ? offline
                    ? 'You’re offline. Your video comes back as soon as you are, on the same link.'
                    : stuck
                      ? 'Still trying to reach the streaming server. Check your connection.'
                      : 'Getting your video back to viewers. Your link stays the same.'
                  : live ? 'Share your link. Viewers can open it right now.' : 'Share it now. It starts playing as soon as your video arrives.'}
              </p>
              <TitleEditor title={currentTitle} onSave={saveTitle} empty="No title" className="mt-2.5 -ml-0.5" />
              {live && current && <StreamStats stream={current} tone="light" className="mt-2" />}
              {notice && <p className="mt-2 text-[13px] font-bold text-ok">{notice}</p>}
              {current && (
                <div className="mt-4">
                  <ListedSwitch listed={current.listed} canList={canList} busy={listing} onChange={(next) => void toggleListed(next)} onSignIn={() => setSigningIn(true)} />
                </div>
              )}
              {!live && followers > 0 && (
                <div className="mt-3">
                  <NotifyFollowersSwitch tone="light" on={notifyFollowers} followers={followers} onChange={switchNotify} />
                </div>
              )}
              {live && told && <p className="mt-2 text-[13px] font-bold text-muted">{told}</p>}
              <div className="mt-4">
                <ShareBar url={stream.watchUrl} compact />
              </div>
              <div className="mt-5 hidden sm:block">{tools(false)}</div>
              <button
                type="button"
                onClick={() => void end()}
                className="speed-text mt-4 h-13 w-full rounded-xl border-2 border-race text-lg text-race hover:bg-race hover:text-white"
              >
                END STREAM
              </button>
            </div>
          )}
          {onAir && current && <ChatPanel stream={current} className="mt-4 hidden h-[max(320px,calc(100dvh-31rem))] rounded-3xl sm:flex" />}

          {phase === 'ended' && (
            <div className="m-2 rounded-[28px] bg-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center text-navy sm:m-0 sm:rounded-3xl sm:p-7">
              <p className="speed-text text-3xl">{removed ? 'Stream removed' : elsewhere ? 'Live somewhere else' : 'Stream ended'}</p>
              {removed ? (
                <p className="mt-1 text-[15px] text-muted">
                  Our review found it breaks the <Link to="/terms" className="font-bold underline">terms of use</Link>.
                </p>
              ) : elsewhere ? (
                <p className="mt-1 text-[15px] text-muted">Your stream continued on another tab or device. Only one can send its video at a time.</p>
              ) : (
                stream && (
                  <>
                    <p className="mt-1 text-[15px] text-muted">Your link stays up so people can see it ended.</p>
                    <TitleEditor title={currentTitle} onSave={saveTitle} empty="No title" className="mt-3 justify-center" />
                  </>
                )
              )}
              {current && !elsewhere && <StreamStats stream={current} tone="light" className="mt-3 justify-center" />}
              {elsewhere && stream ? (
                <button type="button" className="btn-race mt-5 h-15 w-full text-2xl" onClick={() => void resume(stream.id)}>
                  CONTINUE HERE
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-race mt-5 h-15 w-full text-2xl"
                  onClick={() => {
                    setStreamInfo(null);
                    setElsewhere(false);
                    setNotifyFollowers(true);
                    // People may have followed during that stream: the switch shows the new count.
                    void refreshMe();
                    setPhase('ready');
                  }}
                >
                  GO LIVE AGAIN
                </button>
              )}
              {!removed && !elsewhere && stream && !canList && (
                <button type="button" onClick={() => setSigningIn(true)} className="mt-4 block w-full text-[15px] font-bold underline-offset-4 hover:underline">
                  Sign in to keep this replay for 30 days
                </button>
              )}
              <Link to="/account" className="mt-4 block text-[15px] font-bold underline-offset-4 hover:underline">
                Your streams and personal link
              </Link>
              <Link to="/" className="mt-2 block text-sm text-muted underline-offset-4 hover:underline">
                Home
              </Link>
            </div>
          )}
        </div>
      </div>
      {chatSheet && current && onAir && <ChatSheet stream={current} onClose={() => setChatSheet(false)} />}
      {signingIn && (
        <SignInSheet
          intro={onAir ? 'Use your email or phone, no password. Your stream keeps going and moves to your account, on the same link.' : 'Use your email or phone, no password. New here? This creates your account and keeps anything you streamed on this device.'}
          onClose={() => setSigningIn(false)}
          onDone={signedIn}
        />
      )}
    </main>
  );
}

function ToolButton({ label, compact, pressed, disabled, onClick, children }: { label: string; compact: boolean; pressed?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  if (compact) {
    return (
      <button type="button" aria-label={label} aria-pressed={pressed} disabled={disabled} onClick={onClick} className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/45 disabled:opacity-40">
        {children}
      </button>
    );
  }
  return (
    <button type="button" aria-pressed={pressed} disabled={disabled} onClick={onClick} className="flex flex-col items-center gap-1.5 text-xs font-bold disabled:opacity-40">
      <span className={`inline-flex h-13 w-13 items-center justify-center rounded-full ${pressed ? 'bg-white text-navy' : 'bg-white/15 hover:bg-white/25'}`}>{children}</span>
      {label}
    </button>
  );
}

/** m:ss of the broadcast while `on`: since `startedAt` (it survives reconnects and a reloaded page), ticking once a second. */
function useElapsed(on: boolean, startedAt: number | null): string | null {
  const [since, setSince] = useState<number | null>(null);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!on) return setSince(null);
    setSince(Math.min(startedAt ?? Date.now(), Date.now()));
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
  if (since === null) return null;
  const s = Math.floor((Date.now() - since) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}
